import type { InteractionRule } from './actions';

// Declarative wiring between scene events and effects. New behavior is added
// here, not by changing the engine. Each rule's `match` is ANDed; multiple
// rules can match the same event and they all fire.
//
// Per-era beds + music are NOT rules — they're data in public/soundscape.json,
// played by the SoundscapeEngine (era-crossfaded beds + a diegetic music slot).
// So this static list is purely the interaction rules (generator, cassettes,
// boombox).
export const RULES: InteractionRule[] = [
  // ── the generator gate (N8) ──────────────────────────────────────────────
  // Starting the generator is the price of entry: the pull-start latches the
  // session's one-way `powered` state and everything electrical wakes — rules
  // below marked requiresPower, plus soundscape placements marked the same.
  // The start SOUND here is a scratch synth sputter (A13 patch); the real
  // sputter → catch → settle sequence is John's-ear work. The "settle" is the
  // generator BED fading in on its own once powered (level-triggered, fluid).
  // Deliberately NOT requiresPower — it's the source. The SFX fires on every
  // generator click (pull feedback); the POWER state (on, or off in the day's
  // closing window — item 3) is owned by app.ts where the clock + latch are in
  // scope, so this rule no longer carries `power.on`.
  {
    match: { event: 'click', heroId: 'hero_generator' },
    actions: [
      { kind: 'audio.play', id: 'synth_gen_start', channel: 'sfx', volume: 0.8, at: { heroId: 'hero_generator' } },
      { kind: 'log', message: 'generator pulled' },
    ],
  },

  // Any cassette: read its track_id from userData, play on music channel,
  // spatialized to the boombox (audio_source_hero_id userData). exclusive:
  // true means clicking a new cassette fades the previous one out. Cassettes
  // currently only exist in past — when present cassettes get authored,
  // they'll match via the prefix unchanged.
  {
    match: { event: 'click', heroIdPrefix: 'hero_cassette' },
    requiresPower: true, // N8: the boombox is electrical — no power, no tape
    actions: [
      // Take over the boombox's music slot with this cassette's tape. The
      // SoundscapeEngine owns the slot: the tape rides across era changes and
      // plays until the boombox is ejected (it does not auto-resume the era
      // default — that returns on the next era change).
      {
        kind: 'music.override',
        key: 'track_id',
        volume: 0.9,
        at: { heroIdFromUserData: 'audio_source_hero_id' },
      },
      { kind: 'log', message: 'cassette inserted (boombox)' },
    ],
  },

  // Boombox: eject — silence the music slot. The era default returns on the
  // next era change, not immediately.
  {
    match: { event: 'click', heroId: 'hero_boombox' },
    requiresPower: true, // N8: electrical
    actions: [
      { kind: 'music.clear' },
      { kind: 'log', message: 'boombox ejected music' },
    ],
  },
];
